import styled from '@emotion/styled';
import type { ClassificationOutput, EvaluationOutput, QueryMetadata } from '@trinity/chat';
import { ChatResponsePane } from './ResponsePane';
import useToggle from '@trinity/chat-components/hooks/useToggle';
import { Chip } from '../presentation/Chip';

type Props = {
  className?: string;
  metadata: QueryMetadata;
};

export const QueryMetadataPane = (props: Props) => {
  const [open, toggleOpen] = useToggle(false);
  const { metadata } = props;

  return (
    <ChatResponsePane
      className={props.className}
      open={open}
      toggleOpen={toggleOpen}
      title="Query Analysis">
      <Content>
        {metadata.rewrittenQuery && (
          <RewrittenQuerySection query={metadata.rewrittenQuery} />
        )}
        {metadata.evaluation && (
          <EvaluationSection evaluation={metadata.evaluation} />
        )}
        {metadata.classifications && (
          <ClassificationSection classification={metadata.classifications} />
        )}
      </Content>
    </ChatResponsePane>
  );
};

type RewrittenQuerySectionProps = {
  query: string;
};

const RewrittenQuerySection = ({ query }: RewrittenQuerySectionProps) => (
  <Section>
    <SectionTitle>Rewritten Query</SectionTitle>
    <RewrittenQueryText>{query}</RewrittenQueryText>
  </Section>
);

type EvaluationSectionProps = {
  evaluation: EvaluationOutput;
};

const EvaluationSection = ({ evaluation }: EvaluationSectionProps) => (
  <Section>
    <SectionTitle>Evaluation</SectionTitle>
    <EvalGrid>
      <EvalRow>
        <EvalLabel>Completeness</EvalLabel>
        <CompletenessBadge completeness={evaluation.completeness}>
          {evaluation.completeness}
        </CompletenessBadge>
      </EvalRow>
      <EvalRow>
        <EvalLabel>Scope Match</EvalLabel>
        <BooleanBadge value={evaluation.scopeMatch}>
          {evaluation.scopeMatch ? 'Yes' : 'No'}
        </BooleanBadge>
      </EvalRow>
    </EvalGrid>
  </Section>
);

type ClassificationSectionProps = {
  classification: ClassificationOutput;
};

const ClassificationSection = ({ classification }: ClassificationSectionProps) => (
  <Section>
    <SectionTitle>Classification</SectionTitle>
    {classification.topics.length > 0 && (
      <SubSection>
        <SubSectionTitle>Topics</SubSectionTitle>
        <TagList>
          {classification.topics.map((topic, i) => (
            <Chip key={i}>{topic}</Chip>
          ))}
        </TagList>
      </SubSection>
    )}
    {classification.entities.length > 0 && (
      <SubSection>
        <SubSectionTitle>Entities</SubSectionTitle>
        <TagList>
          {classification.entities.map((entity, i) => (
            <EntityChip key={i}>
              <EntityType>{entity.type}</EntityType>
              <EntityValue>{entity.value}</EntityValue>
            </EntityChip>
          ))}
        </TagList>
      </SubSection>
    )}
    {classification.constraints.length > 0 && (
      <SubSection>
        <SubSectionTitle>Constraints</SubSectionTitle>
        <TagList>
          {classification.constraints.map((constraint, i) => (
            <Chip key={i}>
              <ConstraintField>{constraint.field}</ConstraintField>
              <ConstraintOp>{constraint.op}</ConstraintOp>
              <ConstraintValue>{String(constraint.value)}</ConstraintValue>
            </Chip>
          ))}
        </TagList>
      </SubSection>
    )}
  </Section>
);

const Content = styled.div({
  marginTop: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Section = styled.div({});

const SectionTitle = styled.div(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 14,
  marginBottom: 8,
  color: theme.palette.black.light1,
}));

const RewrittenQueryText = styled.p(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.black.light1,
  fontStyle: 'italic',
  margin: 0,
  lineHeight: 1.5,
}));

const SubSection = styled.div({
  marginBottom: 8,
});

const SubSectionTitle = styled.div(({ theme }) => ({
  fontSize: 14,
  fontFamily: theme.fonts.semiBold,
  marginBottom: 6,
  color: theme.palette.black.light1,
}));

const EvalGrid = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
});

const EvalRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const EvalLabel = styled.span(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.black.light1,
  minWidth: 120,
}));

type CompletenessBadgeProps = {
  completeness: EvaluationOutput['completeness'];
};

const completenessColors: Record<EvaluationOutput['completeness'], { bg: string; text: string }> = {
  complete: { bg: '#e6f4ea', text: '#1e7e34' },
  partial: { bg: '#fff3cd', text: '#856404' },
  failed: { bg: '#fde8e8', text: '#c0392b' },
};

const CompletenessBadge = styled.span<CompletenessBadgeProps>(({ completeness }) => ({
  fontSize: 12,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 10,
  textTransform: 'capitalize',
  backgroundColor: completenessColors[completeness].bg,
  color: completenessColors[completeness].text,
}));

type BooleanBadgeProps = {
  value: boolean;
};

const BooleanBadge = styled.span<BooleanBadgeProps>(({ value }) => ({
  fontSize: 12,
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 10,
  backgroundColor: value ? '#e6f4ea' : '#fde8e8',
  color: value ? '#1e7e34' : '#c0392b',
}));

const TagList = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
});

const EntityChip = styled(Chip)({
  padding: 0,
  overflow: 'hidden',
  gap: 0,
});

const EntityType = styled.span(({ theme }) => ({
  padding: '2px 8px',
  backgroundColor: theme.palette.gray.light2,
  color: theme.palette.gray.dark1,
  fontSize: 11,
  textTransform: 'capitalize',
  fontFamily: theme.fonts.semiBold,
}));

const EntityValue = styled.span(({ theme }) => ({
  padding: '2px 8px',
  color: theme.palette.gray.dark1,
}));

const ConstraintField = styled.span(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.dark1,
}));

const ConstraintOp = styled.span(({ theme }) => ({
  color: theme.palette.gray.dark1,
  fontFamily: 'monospace',
}));

const ConstraintValue = styled.span(({ theme }) => ({
  color: theme.palette.gray.dark1,
}));